/**
 * Full Footer
 * 
 * Multi-column footer with all sections including newsletter,
 * quick links, programs, and social media.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Heart, 
  MapPin, 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Twitter, 
  Youtube,
  Linkedin,
  ArrowRight,
  Users,
  Calendar
} from "lucide-react";
import { siteConfig, getEnabledSocialLinks, type SocialPlatform } from "@/lib/site-config";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { newsletterService } from "@/services/newsletterService";
import { toast } from "sonner";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const socialIcons: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
};

const FooterFull = () => {
  const currentYear = new Date().getFullYear();
  const config = useSiteConfig();
  const { features } = useSiteSettings();
  const { currentLayout } = useLayout();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const footerConfig = currentLayout.footer;
  const { footer } = siteConfig;
  const enabledSocialLinks = getEnabledSocialLinks(config.socialLinks);
  const fullAddress = `${config.contact.address.street}, ${config.contact.address.city}, ${config.contact.address.state} ${config.contact.address.zip}`;

  const quickLinks = [
    { name: "About Us", path: "/about-us" },
    ...(features.programs.enabled ? [{ name: "Programs", path: "/programs" }] : []),
    ...(features.events.enabled ? [{ name: "Events", path: "/events" }] : []),
    ...(features.gallery.enabled ? [{ name: "Gallery", path: "/gallery" }] : []),
    ...(features.news.enabled ? [{ name: "News", path: "/news" }] : []),
    ...(features.membership.enabled ? [{ name: "Membership", path: "/membership" }] : []),
    { name: "Contact", path: "/contact" }
  ];

  const programLinks = features.programs.enabled ? [
    { name: "Community Programs", path: "/programs#community" },
    { name: "Performing Arts", path: "/programs#arts" },
    { name: "Educational Programs", path: "/programs#education" },
    { name: "Community Outreach", path: "/programs#outreach" }
  ] : [];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await newsletterService.subscribeToNewsletter({
        email: email.trim(),
      });

      if (result.success) {
        toast.success('Thank you for subscribing to our newsletter!');
        setEmail('');
      } else {
        toast.error(result.error?.getUserMessage() || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Organization Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold">{config.organizationName}</span>
            </div>
            
            <div className="text-primary-foreground/80 [&_.prose]:text-primary-foreground/80">
              <RichTextDisplay content={config.description || ''} prose={false} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-heritage flex-shrink-0" />
                <span className="text-sm">{fullAddress}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-heritage flex-shrink-0" />
                <span className="text-sm">{config.contact.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-heritage flex-shrink-0" />
                <span className="text-sm">{config.contact.email}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          {footerConfig.showQuickLinks && (
            <div>
              <h3 className="text-lg font-bold mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path}
                      className="text-primary-foreground/80 hover:text-heritage transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Programs - Only show if programs feature is enabled */}
          {footer.showPrograms && features.programs.enabled && programLinks.length > 0 && (
            <div>
              <h3 className="text-lg font-bold mb-6">Our Programs</h3>
              <ul className="space-y-3">
                {programLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path}
                      className="text-primary-foreground/80 hover:text-heritage transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Newsletter Signup */}
          {footerConfig.showNewsletter && features.newsletter.enabled && (
            <div>
              <h3 className="text-lg font-bold mb-6">{features.newsletter.title}</h3>
              <p className="text-primary-foreground/80 text-sm mb-4">
                {features.newsletter.description}
              </p>
              
              <div className="space-y-4">
                <form onSubmit={handleNewsletterSubmit} className="flex space-x-2">
                  <Input 
                    type="email"
                    placeholder="Enter your email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60"
                    aria-label="Email address for newsletter"
                    required
                  />
                  <Button 
                    type="submit"
                    variant="heritage" 
                    size="sm" 
                    disabled={isSubmitting}
                    aria-label="Subscribe to newsletter"
                  >
                    {isSubmitting ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4" />
                    )}
                  </Button>
                </form>

                {/* Social Media */}
                {footerConfig.showSocialLinks && enabledSocialLinks.length > 0 && (
                  <div>
                    <p className="text-sm text-primary-foreground/80 mb-3">Follow us on social media</p>
                    <div className="flex space-x-3">
                      {enabledSocialLinks.map((link) => {
                        const IconComponent = socialIcons[link.platform];
                        return (
                          <a 
                            key={link.platform}
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-8 h-8 bg-primary-foreground/10 hover:bg-heritage/20 rounded-full flex items-center justify-center transition-colors"
                            aria-label={`Follow us on ${link.platform}`}
                            tabIndex={0}
                          >
                            <IconComponent className="w-4 h-4" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA Banner */}
      {(features.membership.enabled || features.events.enabled) && (
        <div className="border-t border-primary-foreground/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                {features.membership.enabled ? (
                  <>
                    <h3 className="text-xl font-bold mb-2">Join Our Community Today</h3>
                    <p className="text-primary-foreground/80">
                      Be part of preserving and celebrating our community
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold mb-2">Stay Connected</h3>
                    <p className="text-primary-foreground/80">
                      Explore our upcoming events and programs
                    </p>
                  </>
                )}
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                {features.membership.enabled && (
                  <Button variant="heritage" asChild>
                    <Link to="/membership">
                      <Users className="w-4 h-4 mr-2" />
                      Become a Member
                    </Link>
                  </Button>
                )}
                {features.events.enabled && (
                  <Button variant="culture" asChild>
                    <Link to="/events">
                      <Calendar className="w-4 h-4 mr-2" />
                      View Events
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Footer */}
      <div className="border-t border-primary-foreground/20 bg-primary-darker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-primary-foreground/80">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <p>
                {footer.copyrightText || `© ${currentYear} ${config.organizationName}. All rights reserved.`}
              </p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end space-x-6">
              <Link to="/privacy" className="hover:text-heritage transition-colors">
                Privacy Policy
              </Link>
              <Link to="/accessibility" className="hover:text-heritage transition-colors">
                Accessibility Statement
              </Link>
              <Link to="/contact" className="hover:text-heritage transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterFull;


